'use client';

// The native transfer queue as the Storage & Sync page reads it (BL-2685, ADR 070 — Ingest Protocol):
// its summary and its failed rows, read once on open and again on every change the plugin announces.
// Both reads are local (the plugin), never the network. Outside the app, or in a build without the
// plugin, there is no native queue, which is not an error.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { NativeQueueInput } from '@auctogon/admin-isomorphic/isomorphic/offline/queue-status';
import {
  getNativeDeviceGateway,
  NativeCapabilityUnavailableError,
  type NativeTransferQueue,
  type NativeTransferResolveInput,
} from '../../gateways/native-device';
import type { ChangeQueueActionResult } from '../../stores/change-queue/change-queue-replay';

/** Failed rows read per pass: far more than a person can act on from a phone. */
const FAILED_ROWS_LIMIT = 200;

/** What {@link useNativeTransferStatus} returns. */
export interface NativeTransferStatusValue {
  /** True once the first read finished, whether it found a queue, none, or failed. */
  settled: boolean;
  /** The queue's summary and failed rows, or null when there is no native queue (or it could not be read). */
  native: NativeQueueInput | null;
  /** The last read's failure, or null. */
  error: Error | null;
  /** Read again (the page's Retry after a failed read). */
  refresh: () => void;
  /** Retry or discard failed rows; a refusal is a result, not a throw. */
  resolve: (input: NativeTransferResolveInput) => Promise<ChangeQueueActionResult>;
}

interface ReadState {
  key: string;
  native: NativeQueueInput | null;
  error: Error | null;
}

async function readQueue(queue: NativeTransferQueue): Promise<NativeQueueInput> {
  const summary = await queue.summary();
  const rows = summary.failed > 0 ? await queue.listRows({ states: ['failed'], limit: FAILED_ROWS_LIMIT }) : [];
  return { summary, failedRows: rows.map((r) => ({ id: r.id, label: r.label, errorCode: r.errorCode })) };
}

/** Whether two reads say the same thing, so an unchanged queue does not re-render the shell. */
function sameNative(a: NativeQueueInput | null, b: NativeQueueInput): boolean {
  return a != null && JSON.stringify(a) === JSON.stringify(b);
}

/**
 * @param input.enabled - true inside the native app.
 * @param input.tenantId - the active tenant, or null.
 * @param input.userId - the signed-in user, or null.
 * @returns the native queue's status for that scope.
 * @sideEffect Opens the plugin's queue and subscribes to its change event while mounted.
 */
export function useNativeTransferStatus(input: { enabled: boolean; tenantId: string | null; userId: string | null }): NativeTransferStatusValue {
  const { enabled, tenantId, userId } = input;
  const key = enabled && tenantId != null && userId != null ? `${tenantId}/${userId}` : '';
  const [state, setState] = useState<ReadState | null>(null);
  const queueRef = useRef<NativeTransferQueue | null>(null);
  const readRef = useRef<(() => void) | null>(null);
  // Bumped by Retry when the queue never opened, so the effect opens it again.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (key === '' || tenantId == null || userId == null) return;
    let live = true;
    let unsubscribe: (() => void) | null = null;
    // One read in flight; a change that lands meanwhile asks for exactly one more, so a burst of
    // plugin events is a few reads, applied in order, and an unchanged answer keeps the old state.
    let reading = false;
    let again = false;
    const read = async (queue: NativeTransferQueue): Promise<void> => {
      if (reading) {
        again = true;
        return;
      }
      reading = true;
      try {
        do {
          again = false;
          await readOnce(queue);
        } while (again && live);
      } finally {
        reading = false;
      }
    };
    const readOnce = async (queue: NativeTransferQueue) => {
      try {
        const native = await readQueue(queue);
        if (live) setState((prev) => (prev?.key === key && prev.error == null && sameNative(prev.native, native) ? prev : { key, native, error: null }));
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS).
        console.error('native transfer: could not read the queue', error);
        if (live) setState({ key, native: null, error });
      }
    };
    (async () => {
      const gateway = await getNativeDeviceGateway();
      const queue = await gateway.openNativeTransfer({ tenantId, userId });
      if (!live) return;
      queueRef.current = queue;
      readRef.current = () => void read(queue);
      unsubscribe = queue.onChange(() => void read(queue));
      await read(queue);
    })().catch((err: unknown) => {
      if (!live) return;
      // No plugin in this build (or no app): there is no native queue, which is not a failure.
      if (err instanceof NativeCapabilityUnavailableError) return setState({ key, native: null, error: null });
      const error = err instanceof Error ? err : new Error(String(err));
      // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS).
      console.error('native transfer: could not open the queue', error);
      setState({ key, native: null, error });
    });
    return () => {
      live = false;
      queueRef.current = null;
      readRef.current = null;
      unsubscribe?.();
    };
  }, [key, tenantId, userId, attempt]);

  const refresh = useCallback(() => (readRef.current ? readRef.current() : setAttempt((n) => n + 1)), []);
  const resolve = useCallback(async (resolveInput: NativeTransferResolveInput): Promise<ChangeQueueActionResult> => {
    const queue = queueRef.current;
    if (!queue) return { success: false, code: 'NOT_FOUND' };
    const result = await queue.resolveFailed(resolveInput);
    return result.success ? { success: true } : { success: false, code: result.code };
  }, []);

  const current = key !== '' && state?.key === key ? state : null;
  return { settled: key === '' || current != null, native: current?.native ?? null, error: current?.error ?? null, refresh, resolve };
}
